import { Query, keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useRef, useState } from 'react'

import { PageData, PageMetaData, PageQuery } from '@utils/ApiClient'

export interface PaginatedQueryResult<T> {
  isLoading: boolean
  isFetching: boolean
  isPlaceholderData: boolean
  data: T[]
  meta?: PageMetaData
  pageQuery?: PageQuery
  setPageQuery: (pageQuery?: PageQuery) => void
  hasInitialResults: boolean
  resetState: () => void
  forceRefresh: () => void
}

interface PaginatedData<T> {
  data: T[]
  meta: PageMetaData
}

interface UsePaginatedQueryOptions<T> {
  queryKey: readonly unknown[]
  queryFn: (pageQuery?: PageQuery) => Promise<PageData<T>>
  enabled?: boolean
  refetchInterval?:
    | number
    | false
    | ((query: Query<PaginatedData<T>>) => number | false | undefined)
  refetchOnMount?: boolean
  staleTime?: number
  gcTime?: number
}

// sorts object keys recursively for stable serialization
const sortObjectKeys = (obj: any): any => {
  if (obj === null || typeof obj !== 'object') return obj
  if (Array.isArray(obj)) return obj.map(sortObjectKeys)

  return Object.keys(obj)
    .sort()
    .reduce((result, key) => {
      result[key] = sortObjectKeys(obj[key])
      return result
    }, {} as any)
}

export function getPaginationParams<T>(query: PaginatedQueryResult<T>) {
  const { pageIndex, pageSize, sort, sortOrder } = query.pageQuery ?? {}

  return {
    pageIndex: pageIndex ?? 1,
    pageSize: pageSize ?? 10,
    ...(sort && sortOrder && { sort: `${sort} ${sortOrder}` }),
  }
}

export function usePaginatedQuery<T>(
  options: UsePaginatedQueryOptions<T>
): PaginatedQueryResult<T> {
  const { refetchOnMount = true, refetchInterval } = options

  const [pageQuery, setPageQuery] = useState<PageQuery>()
  const queryClient = useQueryClient()
  const stableEmptyData = useMemo<T[]>(() => [], [])

  const hasInitialResultsRef = useRef<boolean | null>(null)

  const serializedQueryKey = useMemo(
    () => JSON.stringify(sortObjectKeys(options.queryKey)),
    [options.queryKey]
  )
  const stableQueryKey = useMemo(() => options.queryKey, [serializedQueryKey])
  const resetState = useCallback(() => {
    setPageQuery(undefined)
    queryClient.invalidateQueries({ queryKey: stableQueryKey })
  }, [queryClient, stableQueryKey])

  const forceRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: stableQueryKey })
  }, [queryClient, stableQueryKey])

  const serializedPageQuery = useMemo(
    () => (pageQuery ? JSON.stringify(sortObjectKeys(pageQuery)) : null),
    [pageQuery]
  )

  const fullKey = [...stableQueryKey, serializedPageQuery]

  const { data, isLoading, isFetching, isPlaceholderData } = useQuery({
    queryKey: fullKey,
    queryFn: async () => {
      const { items, pageIndex, pageSize, totalPages, totalResults, itemsCount } =
        await options.queryFn(pageQuery)

      return {
        data: items,
        meta: {
          pageIndex,
          pageSize,
          totalPages,
          totalResults,
          itemsCount,
        },
      }
    },
    enabled: options.enabled,
    staleTime: options.staleTime,
    gcTime: options.gcTime,
    placeholderData: keepPreviousData,
    retry: false,
    refetchInterval: refetchInterval,
    refetchOnMount: refetchOnMount,
  })

  if (hasInitialResultsRef.current === null && !isLoading && pageQuery === undefined) {
    hasInitialResultsRef.current = (data?.data?.length ?? 0) > 0
  }

  return {
    isLoading,
    isFetching,
    isPlaceholderData,
    data: data?.data ?? stableEmptyData,
    meta: data?.meta,
    pageQuery,
    setPageQuery,
    hasInitialResults: hasInitialResultsRef.current ?? false,
    resetState,
    forceRefresh,
  }
}
